'use client';

import { forwardRef } from 'react';
import { X } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { TAG_COLORS, getTagById, getTagBySlug } from '@/data/forum-tags';

/**
 * TagBadge - Display component for forum tags
 *
 * Features:
 * - Multiple size variants
 * - Optional remove button
 * - Click handler support
 * - Dynamic colors based on tag type
 */

const tagBadgeVariants = cva(
  'inline-flex items-center gap-1 font-medium transition-colors rounded-full border',
  {
    variants: {
      size: {
        xs: 'text-[10px] px-1.5 py-0.5',
        sm: 'text-xs px-2 py-0.5',
        md: 'text-sm px-2.5 py-1',
        lg: 'text-base px-3 py-1.5',
      },
      clickable: {
        true: 'cursor-pointer hover:opacity-80',
        false: '',
      },
    },
    defaultVariants: {
      size: 'sm',
      clickable: false,
    },
  }
);

/**
 * Get color classes for a tag
 */
const getColorClasses = (tag, colorOverride) => {
  if (colorOverride) {
    return colorOverride;
  }

  // If tag has colorClasses defined
  if (tag?.colorClasses) {
    return tag.colorClasses;
  }

  // Try to find tag in static data
  const staticTag = tag?.id
    ? getTagById(tag.id)
    : tag?.slug
    ? getTagBySlug(tag.slug)
    : null;

  if (staticTag?.colorClasses) {
    return staticTag.colorClasses;
  }

  // Default fallback
  return TAG_COLORS.GRAY;
};

/**
 * TagBadge component
 *
 * @param {object} props
 * @param {object|string} props.tag - Tag object or tag ID
 * @param {string} props.size - 'xs' | 'sm' | 'md' | 'lg'
 * @param {boolean} props.removable - Show remove button
 * @param {function} props.onRemove - Called when remove is clicked
 * @param {function} props.onClick - Called when tag is clicked
 * @param {object} props.colorOverride - Override color classes { bg, text, border }
 * @param {boolean} props.showIcon - Show tag icon
 * @param {string} props.className - Additional classes
 */
export const TagBadge = forwardRef(
  (
    {
      tag,
      size = 'sm',
      removable = false,
      onRemove,
      onClick,
      colorOverride,
      showIcon = false,
      className,
      ...props
    },
    ref
  ) => {
    // Resolve tag data
    const resolvedTag =
      typeof tag === 'string' ? getTagById(tag) || getTagBySlug(tag) : tag;

    if (!resolvedTag) {
      // Unknown tag - show as gray
      return (
        <span
          ref={ref}
          className={cn(
            tagBadgeVariants({ size, clickable: !!onClick }),
            TAG_COLORS.GRAY.bg,
            TAG_COLORS.GRAY.text,
            TAG_COLORS.GRAY.border,
            className
          )}
          onClick={onClick}
          {...props}
        >
          {typeof tag === 'string' ? tag : 'Unknown'}
        </span>
      );
    }

    const colors = getColorClasses(resolvedTag, colorOverride);

    return (
      <span
        ref={ref}
        className={cn(
          tagBadgeVariants({ size, clickable: !!onClick }),
          colors.bg,
          colors.text,
          colors.border,
          className
        )}
        onClick={onClick}
        {...props}
      >
        {showIcon && resolvedTag.icon && (
          <span className="mr-0.5">{resolvedTag.icon}</span>
        )}
        {resolvedTag.name}
        {removable && onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(resolvedTag);
            }}
            className="ml-0.5 rounded-full p-0.5 hover:bg-white/10 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  }
);

TagBadge.displayName = 'TagBadge';

/**
 * TagBadgeList - Display multiple tags
 */
export const TagBadgeList = ({
  tags = [],
  size = 'sm',
  maxVisible = 5,
  showOverflow = true,
  removable = false,
  onRemove,
  onTagClick,
  showIcon = false,
  className,
  tagClassName,
}) => {
  const visibleTags = maxVisible ? tags.slice(0, maxVisible) : tags;
  const overflowCount = tags.length - visibleTags.length;

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {visibleTags.map((tag, index) => {
        const tagId =
          typeof tag === 'string' ? tag : tag.id || tag.tagId || index;
        return (
          <TagBadge
            key={tagId}
            tag={tag}
            size={size}
            removable={removable}
            onRemove={onRemove}
            onClick={onTagClick ? () => onTagClick(tag) : undefined}
            showIcon={showIcon}
            className={tagClassName}
          />
        );
      })}
      {showOverflow && overflowCount > 0 && (
        <span
          className={cn(
            tagBadgeVariants({ size }),
            TAG_COLORS.GRAY.bg,
            TAG_COLORS.GRAY.text,
            TAG_COLORS.GRAY.border
          )}
        >
          +{overflowCount} more
        </span>
      )}
    </div>
  );
};

/**
 * StatusBadge - Specialized badge for status tags
 */
export const StatusBadge = ({ status, size = 'sm', className, ...props }) => {
  const statusTags = {
    solved: { name: 'Solved', colorClasses: TAG_COLORS.GREEN, icon: '✓' },
    open: { name: 'Open', colorClasses: TAG_COLORS.BLUE, icon: '○' },
    closed: { name: 'Closed', colorClasses: TAG_COLORS.GRAY, icon: '●' },
    'in-progress': {
      name: 'In Progress',
      colorClasses: TAG_COLORS.YELLOW,
      icon: '⏳',
    },
  };

  const tagData = statusTags[status] || statusTags.open;

  return (
    <TagBadge
      tag={tagData}
      size={size}
      showIcon
      className={className}
      {...props}
    />
  );
};

/**
 * DifficultyBadge - Specialized badge for difficulty tags
 */
export const DifficultyBadge = ({
  difficulty,
  size = 'sm',
  className,
  ...props
}) => {
  const difficultyTags = {
    beginner: { name: 'Beginner', colorClasses: TAG_COLORS.GREEN, icon: '🌱' },
    intermediate: {
      name: 'Intermediate',
      colorClasses: TAG_COLORS.YELLOW,
      icon: '🌿',
    },
    advanced: { name: 'Advanced', colorClasses: TAG_COLORS.ORANGE, icon: '🌳' },
    expert: { name: 'Expert', colorClasses: TAG_COLORS.RED, icon: '🔥' },
  };

  const tagData = difficultyTags[difficulty] || difficultyTags.beginner;

  return (
    <TagBadge
      tag={tagData}
      size={size}
      showIcon
      className={className}
      {...props}
    />
  );
};

export default TagBadge;
